import {
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  Grid,
  HStack,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Switch,
  useDisclosure
} from '@chakra-ui/react'
import { CustomCollapse, RadioGroupFormControl } from 'BrokerTsLib/src/components'
import type { InsuranceParamsPropertyDto } from 'BrokerTsLib/src/dtos'
import { Insurer, Selection, SelectionType } from 'BrokerTsLib/src/enums'
import { useFormContext } from 'BrokerTsLib/src/rhf'
import { Link } from 'react-router-dom'

import { CustomNumberInput, FormLabel } from '@/components'
import { HorizontalRadios } from '@/components/HorizontalRadios'
import RuianAddressSelector from '@/components/RuianAddressSelector'
import { FormRow } from '@/containers/forms/FormRow'
import { useInsuranceParams } from '@/context/InsuranceParamsProvider'
import { useInsurers, useSelections } from '@/hooks'
import { useNameof } from '@/hooks/useNameof'
import { Search } from '@/icons'

export const LocationForm = () => {
  const { isDisabled } = useInsuranceParams()
  const nameof = useNameof<InsuranceParamsPropertyDto>()

  const { isInsurerAvailable } = useInsurers(false, null)
  const { data: dataBasementArea } = useSelections(SelectionType.PlochaPodsklepení)

  const methods = useFormContext<InsuranceParamsPropertyDto>()

  const {
    register,
    formState: { errors },
    watch,
    setValue,
    getValues,
  } = methods

  const selectedRealtyType = watch('property.data.realtyTypeId')
  const houseWithBasementValue = watch('property.data.hasBasement')
  const isGarageAtOtherLocationAvailable = isInsurerAvailable(Insurer.CPP) || isInsurerAvailable(Insurer.Direct)

  const { isOpen: isOpenApartment } = useDisclosure({
    isOpen: !!selectedRealtyType && (selectedRealtyType == Selection.Byt || selectedRealtyType == Selection.NajemniByt),
  })

  const { isOpen: isOpenGarageAtOtherLocation } = useDisclosure({
    isOpen: watch('property.data.hasGarageOnDifferentLocation') == true,
  })

  const { isOpen: isOpenBasement } = useDisclosure({
    isOpen: houseWithBasementValue == true,
  })

  return (
    <Grid rowGap={8} w='100%'>
      <Flex>
        <Button
          variant="link"
          as={Link}
          to="https://nahlizenidokn.cuzk.cz/"
          target="_blank"
        >
          <Search color="secondary.500" /> Náhled do katastru nemovitostí
        </Button>
      </Flex>

      <RuianAddressSelector
        addressPath={nameof((x) => x.property.data.address)}
        isDisabled={isDisabled}
        showIsOutsideUrbanArea={isInsurerAvailable(Insurer.Allianz)}
        separatedCityAndCityPart={true}
      />

      <CustomCollapse in={!!selectedRealtyType && !isOpenApartment}>
        <Stack gap={4}>
          <RadioGroupFormControl name='property.data.hasBasement' valueAsBoolean isRequired
            label='Je dům podsklepený'
            isDisabled={isDisabled}
          >
            <HorizontalRadios options={[{ value: 'true', label: 'Ano' }, { value: 'false', label: 'Ne' }]} />
          </RadioGroupFormControl>

          <CustomCollapse in={isOpenBasement}>
            <Grid rowGap={8} w='100%' backgroundColor='gray.100' borderRadius='sm' p={6}>
              <FormRow>
                <FormControl
                  isRequired
                  isInvalid={
                    !!errors.property?.data?.realty?.basementArea
                  }
                >
                  <FormLabel tooltip="Plocha podsklepení">
                    Plocha podsklepení
                  </FormLabel>
                  <Select
                    {...register('property.data.realty.basementArea')}
                    defaultValue={''}
                    isDisabled={isDisabled}
                  >
                    <option disabled value="">
                      Vyberte plochu podsklepení
                    </option>
                    {dataBasementArea?.data.map((item) => (
                      <option
                        value={item.id.toString()}
                        key={item.id.toString()}
                      >
                        {item.description}
                      </option>
                    ))}
                  </Select>

                  {errors.property?.data?.realty?.basementArea && (
                    <FormErrorMessage>
                      {
                        errors.property?.data?.realty?.basementArea
                          .message
                      }
                    </FormErrorMessage>
                  )}
                </FormControl>
              </FormRow>
            </Grid>
          </CustomCollapse>
        </Stack>
      </CustomCollapse>

      <CustomCollapse in={isOpenApartment}>
        <RadioGroupFormControl name='property.data.hasNonresidentialPremises' valueAsBoolean isRequired
          label='Nebytové prostory'
          labelProps={{ hint: 'Nejčastěji sklepní kóje nebo garáž' }}
          isDisabled={isDisabled}
        >
          <HorizontalRadios options={[{ value: 'true', label: 'Ano' }, { value: 'false', label: 'Ne' }]} />
        </RadioGroupFormControl>
      </CustomCollapse>

      <CustomCollapse in={isGarageAtOtherLocationAvailable}>
        <FormControl
          isRequired
          isInvalid={
            !!errors.property?.data?.hasGarageOnDifferentLocation
          }
        >
          <FormLabel tooltip="Garáž se nachází na jiné adrese než místo pojištění.">
            Garáž na jiné adrese
          </FormLabel>

          <RadioGroup
            isDisabled={isDisabled}
            value={
              watch('property.data.hasGarageOnDifferentLocation') == true
                ? 'yes'
                : watch('property.data.hasGarageOnDifferentLocation') == false ? 'no' : undefined
            }
            onChange={(val) =>
              setValue('property.data.hasGarageOnDifferentLocation', val === 'yes')
            }
          >
            <HStack gap={6}>
              <Radio value="yes" gap={3}>Ano</Radio>
              <Radio value="no" gap={3}>Ne</Radio>
            </HStack>
          </RadioGroup>

          {errors.property?.data?.hasGarageOnDifferentLocation && (
            <FormErrorMessage>
              {
                errors.property?.data?.hasGarageOnDifferentLocation
                  .message
              }
            </FormErrorMessage>
          )}
        </FormControl>
      </CustomCollapse>

      <CustomCollapse in={isOpenGarageAtOtherLocation}>
        <Grid rowGap={8} w='100%' backgroundColor='gray.100' p={7}>
          <FormRow>
            <FormControl isInvalid={!!errors.property?.data?.garageAtOtherLocationAmount} isRequired>
              <FormLabel tooltip="Pojistná částka budovy garáže">
                Pojistná částka
              </FormLabel>

              <CustomNumberInput
                width='100%'
                defaultValue={getValues('property.data.garageAtOtherLocationAmount')}
                onValueChange={(_, value) => setValue('property.data.garageAtOtherLocationAmount', value)}
                isDisabled={isDisabled}
                inputRightElement={'Kč'}
                inputProps={{
                  placeholder: 'Např. 1 500 000'
                }}
              />

              {errors.property?.data?.garageAtOtherLocationAmount && <FormErrorMessage>{errors.property?.data?.garageAtOtherLocationAmount.message}</FormErrorMessage>}
            </FormControl>
          </FormRow>

          <RuianAddressSelector
            addressPath={nameof((x) => x.property.data.garageAddress)}
            isDisabled={isDisabled}
            separatedCityAndCityPart={true}
          />

          {watch('property.data.garageAddress.isOutsideUrbanAreaAvailable') == true && (
            <FormControl display='flex' alignItems='center'>
              <Switch
                id="garageOutsideUrbanArea"
                {...register('property.data.garageAddress.isOutsideUrbanArea')}
                isChecked={watch('property.data.garageAddress.isOutsideUrbanArea')}
                isDisabled={isDisabled}
              />
              <FormLabel
                htmlFor="garageOutsideUrbanArea"
                variant="switch"
                tooltip="Pro pojišťovnu Allianz: místo pojištění se nachází mimo obec."
              >
                Místo se nachází mimo obec
              </FormLabel>
            </FormControl>
          )}
        </Grid>
      </CustomCollapse>

    </Grid >
  )
}